import * as stylex from '@stylexjs/stylex';
import { FC, memo, useMemo, useState, useEffect } from 'react';
import { styles } from './styles';
import { Ticket } from '@pages/event/api/useFetchTicketSegment';
import { EventSettings } from '@features/api/use-get-settings';
import { ConfirmationAlert } from '../event-participant-card/ui/confirmation-alert';
import { ParticipantInfo } from './ui/participant-info';
import { ContactDataBlock } from './ui/contact-data-block';
import { InvitationStatusSection } from './ui/invitation-status-section';
import { TicketStatusSection } from './ui/ticket-status-section';
import { ParticipantActions } from './ui/participant-actions';
import { GuestsTree } from './ui/guests-tree';
import { useInvitationStatus } from './lib/useInvitationStatus';
import { Drawer } from '@shared/ui/drawer';
import { GuestInfoDrawer, GuestType } from './ui/guest-info-drawer';
import { GuestInfoDrawerSkeleton } from './ui/guest-info-drawer/skeleton';
import { useGetSubmissionByTicketId } from './api';
import { Checkbox } from './model';

interface ParticipantCardNoSegmentProps {
  ticket: Ticket;
  name: string;
  position?: string;
  onDelete?: () => void;
  getTicketQR?: () => void;
  checked?: boolean;
  disabled?: boolean;
  onToggle?: () => void;
  eventDateEnd?: string;
  eventDateStart?: string;
  eventId?: number;
  refetchSegments?: () => void;
  refetchCategories?: () => void;
  onConfirmStatusChange?: () => void;
  toggleUser?: (ticket: Ticket) => void;
  handleDelete?: (ticket: Ticket) => Promise<void> | void;
  checkedUsers?: { [key: number]: number };
  isGuest?: boolean;
  eventSettings?: EventSettings | null;
}

export const ParticipantCardNoSegment: FC<ParticipantCardNoSegmentProps> = memo(
  ({
    name,
    position,
    ticket,
    onDelete,
    getTicketQR,
    checked,
    disabled,
    onToggle,
    eventDateEnd,
    eventDateStart,
    eventId,
    refetchSegments,
    refetchCategories,
    onConfirmStatusChange,
    toggleUser,
    handleDelete,
    checkedUsers,
    isGuest = false,
    eventSettings,
  }) => {
    const [isGuestsExpanded, setIsGuestsExpanded] = useState(false);
    const [isInfoDrawerOpen, setIsInfoDrawerOpen] = useState(false);
    const [scrollToGuest, setScrollToGuest] = useState<{ type: GuestType; index?: number } | undefined>(undefined);
    const { data: submissionData, fetching: isSubmissionFetching } = useGetSubmissionByTicketId(
      ticket.id,
      !isInfoDrawerOpen
    );

    const {
      inviteStatus,
      isDropdownOpen,
      showConfirmationAlert,
      dropdownRef,
      confirmationAlertRef,
      updateFetching,
      setIsDropdownOpen,
      handleSelectAccepted,
      handleDeclineStatusChange,
      handleConfirmStatusChange,
      handleCancelStatusChange,
    } = useInvitationStatus({
      ticket,
      eventDateEnd,
      refetchSegments,
      refetchCategories,
      onConfirmStatusChange,
    });

    const hasGuests = Boolean(
      ticket.dependents && (ticket.dependents.countExtraAdult || ticket.dependents.countExtraChildren)
    );
    const shouldShowLine = isGuestsExpanded && hasGuests;

    const adaptedSubmissionData = useMemo(() => {
      if (!submissionData) return null;

      const adaptCheckbox = (checkbox: Checkbox) => ({
        ...checkbox,
        titleCheckbox: checkbox.titleCheckboxes,
      });

      return {
        mainGuest: submissionData.mainGuest,
        adultGuests: submissionData.adultGuests,
        childrenGuests: submissionData.childrenGuests,
        checkboxes: submissionData.checkboxes.map(adaptCheckbox),
        radiobuttons: submissionData.radiobuttons,
      };
    }, [submissionData]);

    const handleInfoClick = () => {
      if (!isGuest && !hasGuests) return;
      setIsInfoDrawerOpen(true);
    };

    const isDrawerOpen = isInfoDrawerOpen && (isGuest || hasGuests);

    const drawerContent = (() => {
      if (isSubmissionFetching) {
        return <GuestInfoDrawerSkeleton />;
      }
      if (!adaptedSubmissionData) {
        return null;
      }

      return (
        <GuestInfoDrawer
          mainGuest={adaptedSubmissionData.mainGuest}
          adultGuests={adaptedSubmissionData.adultGuests}
          childrenGuests={adaptedSubmissionData.childrenGuests}
          checkboxes={adaptedSubmissionData.checkboxes}
          radiobuttons={adaptedSubmissionData.radiobuttons}
          scrollToGuest={scrollToGuest}
        />
      );
    })();

    useEffect(() => {
      if (!isInfoDrawerOpen || !adaptedSubmissionData || scrollToGuest) return;

      const currentTicketId = ticket.id;

      if (adaptedSubmissionData.mainGuest.ticketId === currentTicketId) {
        setScrollToGuest({ type: 'main' });
        return;
      }

      const adultIndex = adaptedSubmissionData.adultGuests.findIndex((guest) => guest.ticketId === currentTicketId);
      if (adultIndex !== -1) {
        setScrollToGuest({ type: 'adult', index: adultIndex });
        return;
      }

      const childIndex = adaptedSubmissionData.childrenGuests.findIndex((guest) => guest.ticketId === currentTicketId);
      if (childIndex !== -1) {
        setScrollToGuest({ type: 'child', index: childIndex });
      }
    }, [isInfoDrawerOpen, adaptedSubmissionData, ticket.id, scrollToGuest]);

    return (
      <div
        {...stylex.props(
          styles.container,
          shouldShowLine && styles.containerWithGuests,
          disabled && styles.disabledRow
        )}
      >
        <div {...stylex.props(styles.containerTab)}>
          <ParticipantInfo
            name={name}
            position={position}
            ticket={ticket}
            checked={checked}
            disabled={disabled}
            onToggle={() => toggleUser?.(ticket) ?? onToggle?.()}
            hasGuests={hasGuests}
            isGuestsExpanded={isGuestsExpanded}
            onToggleGuests={() => setIsGuestsExpanded(!isGuestsExpanded)}
          />

          <div {...stylex.props(styles.statusAndActionsBlock)}>
            <div {...stylex.props(styles.statusWrapper)}>
              <div {...stylex.props(styles.statuses)}>
                <ContactDataBlock phone={ticket.participant?.phone} email={ticket.participant?.email} />
                <InvitationStatusSection
                  ticket={ticket}
                  inviteStatus={inviteStatus}
                  isDropdownOpen={isDropdownOpen}
                  sendTicketAfterAcceptAt={eventSettings?.sendTicketAfterAcceptAt}
                  dropdownRef={dropdownRef}
                  onToggleDropdown={() => setIsDropdownOpen(!isDropdownOpen)}
                  onCloseDropdown={() => setIsDropdownOpen(false)}
                  onSelectAccepted={handleSelectAccepted}
                  onSelectDeclined={handleDeclineStatusChange}
                  eventDateEnd={eventDateEnd}
                />
                <TicketStatusSection ticket={ticket} eventDateStart={eventDateStart} />
              </div>
              {showConfirmationAlert && (
                <div ref={confirmationAlertRef}>
                  <ConfirmationAlert
                    sendTicketAfterAcceptAt={eventSettings?.sendTicketAfterAcceptAt}
                    onCancel={handleCancelStatusChange}
                    onConfirm={() => handleConfirmStatusChange()}
                    isLoading={updateFetching}
                  />
                </div>
              )}
            </div>
            <ParticipantActions
              ticket={ticket}
              onDelete={onDelete}
              getTicketQR={getTicketQR}
              onInfoClick={handleInfoClick}
            />
          </div>
        </div>
        {isGuestsExpanded && ticket.dependents && (
          <GuestsTree
            ticket={ticket}
            checkedUsers={checkedUsers}
            toggleUser={toggleUser}
            handleDelete={handleDelete}
            eventId={eventId}
            refetchSegments={refetchSegments}
            refetchCategories={refetchCategories}
            eventDateEnd={eventDateEnd}
            eventDateStart={eventDateStart}
            eventSettings={eventSettings}
          />
        )}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={() => {
            setIsInfoDrawerOpen(false);
            setScrollToGuest(undefined);
          }}
          position="right"
          title="Гости"
          drawerPadding="0px 28px 8px 28px"
          headerPadding="28px 0px"
        >
          {drawerContent}
        </Drawer>
      </div>
    );
  }
);
